import React, { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Link from 'redux-first-router-link'
import config from 'config'
import Menu from '@groovehq/internal-design-system/lib/components/Dropdown/DropdownMenu'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import { divider } from '@groovehq/internal-design-system/lib/styles/elements'
import { selectAccountPreferenceDebugFrontend } from 'selectors/app/selectAccountPreferences'
import {
  selectCurrentUser,
  selectCurrentUserCanAccessAdmin,
  selectCurrentUserIsAdminOrOwner,
  selectCurrentUserIsOwner,
} from 'ducks/currentUser/selectors'
import DebugButton from 'components/DebugButton'
import debug from 'util/debug'
import useFrillWidget from 'util/hooks/useFrillWidget'
import LegacyLink from 'components/LegacyLink'
import Avatar from 'components/Avatar'
import { LOGOUT_PAGE } from 'constants/pages'
import {
  SETTINGS_PROFILE_PAGE,
  SETTINGS_BILLING_PAGE,
} from 'subapps/settings/types'
import { selectSettingsLandingPageAction } from 'selectors/menus'
import {
  profile as ProfileIcon,
  billing as BillingIcon,
  settings as SettingsIcon,
  inviteTeammate as InviteTeammateIcon,
  logout as LogoutIcon,
  announcements as AnnouncementsIcon,
  feature as FeatureIcon,
} from 'assets/icons/groove/v2'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_TYPE_AGENT_ADD } from 'ducks/drawers/types'
import GrooveStatusMenuItem from './GrooveStatusMenuItem'
import ItemWithIcon from './ItemWithIcon'
import { styles as helpItemStyles } from '../HelpItem/styles'
import { styles } from './styles'

const TO_SETTINGS_PROFILE_PAGE = {
  type: SETTINGS_PROFILE_PAGE,
}

const TO_SETTINGS_BILLING_PAGE = {
  type: SETTINGS_BILLING_PAGE,
}

const FullMenu = React.memo(
  ({
    announcementCount,
    onClickAnnoucementsLauncher,
    onClickIdeasLauncher,
    showAnnoucementItem,
  }) => {
    const dispatch = useDispatch()
    const canDebugFrontend = useSelector(selectAccountPreferenceDebugFrontend)
    const isDebug = canDebugFrontend || debug.enabled
    const isSuperAdmin = useSelector(selectCurrentUserCanAccessAdmin)
    const isManager = useSelector(selectCurrentUserIsAdminOrOwner)
    const isOwner = useSelector(selectCurrentUserIsOwner)
    const settingsLandingPageAction = useSelector(
      selectSettingsLandingPageAction
    )
    const hasBottomItems = isSuperAdmin || isDebug
    // We need to pass user details to Frill widgets
    // so need to make sure the user data exist
    const currentUser = useSelector(selectCurrentUser)

    const { openDrawer: openAddDrawer } = useDrawer({
      type: DRAWER_TYPE_AGENT_ADD,
      lazyLoad: true,
    })

    const handleAddTeammates = useCallback(
      () => {
        openAddDrawer('new', {
          additionalProps: { title: `Add ${app.t('Agent')}` },
        })
      },
      [openAddDrawer]
    )

    const handleLogout = useCallback(
      () => {
        dispatch({ type: LOGOUT_PAGE })
      },
      [dispatch]
    )

    return (
      <Menu as="div" css={styles.menuStyle}>
        <Link className="grui" to={TO_SETTINGS_PROFILE_PAGE}>
          <Menu.Item itemKey="my-profile" className="grui">
            <ItemWithIcon icon={<ProfileIcon />}>View profile</ItemWithIcon>
          </Menu.Item>
        </Link>
        <Link className="grui" to={settingsLandingPageAction}>
          <Menu.Item itemKey="settings" className="grui">
            <ItemWithIcon icon={<SettingsIcon />}>Settings</ItemWithIcon>
          </Menu.Item>
        </Link>
        {isOwner && (
          <Link className="grui" to={TO_SETTINGS_BILLING_PAGE}>
            <Menu.Item itemKey="billing" className="grui">
              <ItemWithIcon icon={<BillingIcon />}>Billing</ItemWithIcon>
            </Menu.Item>
          </Link>
        )}
        {isManager && (
          <Menu.Item
            itemKey="invite-teammates"
            className="grui"
            onClick={handleAddTeammates}
            css={styles.highlightMenuItem}
          >
            <ItemWithIcon icon={<InviteTeammateIcon className="sm-icon" />}>
              {`Invite ${app.t('team')} members`}
            </ItemWithIcon>
          </Menu.Item>
        )}
        <div css={divider.styles.primary} role="separator" />
        {currentUser?.email &&
          showAnnoucementItem && (
            <Menu.Item
              itemKey="announcements"
              className="grui"
              onClick={onClickAnnoucementsLauncher}
            >
              <ItemWithIcon icon={<AnnouncementsIcon className="sm-icon" />}>
                Announcements
                {announcementCount > 0 && (
                  <span css={helpItemStyles.badge} className="grui ml-auto">
                    {announcementCount}
                  </span>
                )}
              </ItemWithIcon>
            </Menu.Item>
          )}
        {currentUser?.email && (
          <Menu.Item
            itemKey="request-feature"
            className="grui"
            onClick={onClickIdeasLauncher}
          >
            <ItemWithIcon icon={<FeatureIcon className="sm-icon" />}>
              Request a feature
            </ItemWithIcon>
          </Menu.Item>
        )}
        <GrooveStatusMenuItem />
        <div css={divider.styles.primary} role="separator" />
        <Menu.Item
          itemKey="logout"
          as="button"
          type="button"
          onClick={handleLogout}
        >
          <ItemWithIcon icon={<LogoutIcon className="sm-icon" />}>
            Log out
          </ItemWithIcon>
        </Menu.Item>
        {hasBottomItems && (
          <div css={divider.styles.primary} role="separator" />
        )}
        {isSuperAdmin && (
          <>
            <LegacyLink
              path="/admin/accounts"
              className="grui"
              async={false}
              skipModal
            >
              <Menu.Item itemKey="go-to-admin" className="grui">
                Go to admin
              </Menu.Item>
            </LegacyLink>
            <LegacyLink
              path="/sidekiq"
              className="grui"
              async={false}
              skipModal
            >
              <Menu.Item itemKey="go-to-sidekiq" className="grui">
                Sidekiq Dashboard
              </Menu.Item>
            </LegacyLink>
          </>
        )}
        {isDebug && <DebugButton as={Menu.Item} />}
      </Menu>
    )
  }
)

const FRILL_WIDGETS = config.frillWidgets
const SHOW_FRILL_ANNOUNCEMENTS = config.showFrillAnnoucements

const SettingsMenu = React.memo(() => {
  const currentUser = useSelector(selectCurrentUser)
  const userRef = useRef()
  const [announcementCount, setAnnouncementCount] = useState()

  const { toggle: handleClickAnnoucementsLauncher } = useFrillWidget(
    SHOW_FRILL_ANNOUNCEMENTS ? FRILL_WIDGETS.announcements.key : null,
    { onBadgeCount: setAnnouncementCount }
  )

  const { toggle: handleClickIdeasLauncher } = useFrillWidget(
    FRILL_WIDGETS.ideas.key,
    { onBadgeCount: setAnnouncementCount }
  )

  return (
    <Dropdown
      overlay={
        <FullMenu
          setAnnouncementCount={setAnnouncementCount}
          announcementCount={announcementCount}
          onClickAnnoucementsLauncher={handleClickAnnoucementsLauncher}
          onClickIdeasLauncher={handleClickIdeasLauncher}
          showAnnoucementItem={SHOW_FRILL_ANNOUNCEMENTS}
        />
      }
      position="bottom-end"
      strategy="fixed"
      offset={8}
      autoHeight
      menuItemIconHidden
    >
      <div css={styles.avatarContainer} className="grui ml-7">
        <div ref={userRef} className="grui flex items-center">
          <Avatar size={32} actor={currentUser} />
        </div>
      </div>
    </Dropdown>
  )
})

export default SettingsMenu
